/**
 * GimbalPortal REST client.
 *
 * Unified auth (ADR-0004): the httpOnly cookie `auth_token` carries the
 * user's API key for browser requests. Agents use the same key as
 * `Authorization: Bearer` for MCP access.
 */

const API_BASE = '/api';

export interface SpaceSummary {
  id: string;
  title: string;
  description: string | null;
  featuredImages: string[];
  ueClientDownloadUrl: string | null;
  inviteCode: string | null;
  inviteCodeGeneratedAt: string | null;
  inviteCodeUsedAt: string | null;
  authToken: string | null;
  status: number;
  status_label: string;
  ueVersion: string | null;
  actorCount: number;
  commandCount: number;
  lastHeartbeatAt: string | null;
  scheduledT0At: string | null;
  scheduledT0Armed: boolean;
  currentSessionConnectedAt: string | null;
  lateFirePolicy: 'drop' | 'run_late';
  publicType: number;
}

export interface ScheduledIntent {
  id: string;
  spaceId: string;
  userId: string | null;
  actorId: string | null;
  intentType: string;
  params: Record<string, unknown>;
  scheduledAtRelativeMs: number;
  t0Source: 'operator' | 'connect_time';
  t0At: string;
  fireAtUnixMs: number;
  spaceScheduledT0Revision: number | null;
  status: number;
  result: Record<string, unknown> | null;
  error: string | null;
  createdAt: string;
  firedAt: string | null;
  completedAt: string | null;
}

export interface ActorRow {
  id: string;
  spaceId: string;
  name: string;
  classHint: string | null;
  introduction: string | null;
  imageUrl: string | null;
  actorType: number;
  ueName: string | null;
  ueClass: string | null;
  uePath: string | null;
  properties: Record<string, unknown>;
  tags: string[];
  status: number;
  mobility: number | null;
}

export interface CommandLogRow {
  id: string;
  spaceId: string | null;
  actorId: string | null;
  userId: string | null;
  command: string;
  params: Record<string, unknown>;
  status: number;
  result: Record<string, unknown> | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface SpaceStatus {
  id: string;
  status: number;
  status_label: string;
  ue_version: string | null;
  last_heartbeat_at: string | null;
  age_seconds: number | null;
}

export interface ActorAssignmentRow {
  id: string;
  user_id: string;
  user_display_name: string | null;
  actor_id: string;
  assigned_at: string;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${body || '(no body)'}`);
  }
  return (await res.json()) as T;
}

export interface ServiceInfo {
  ok: boolean;
  service: string;
  wsPortRange: number[];
}

export const api = {
  /**
   * Probe the mcp server's `/api` index. Returns the parsed JSON on
   * success (including `wsPortRange` so the portal can offer only
   * valid port choices when creating/editing a Space). Throws on
   * network failure or non-2xx — callers should handle that and
   * surface a "service unreachable" message rather than a raw fetch
   * error.
   */
  async getServiceInfo(): Promise<ServiceInfo> {
    return await request<ServiceInfo>('');
  },
  async listSpaces(): Promise<SpaceSummary[]> {
    return await request<SpaceSummary[]>('/spaces');
  },
  async createSpace(input: {
    title: string;
    description?: string;
    featured_images?: string[];
    ue_client_download_url?: string;
    public_type?: number;
  }): Promise<SpaceSummary> {
    return await request<SpaceSummary>('/spaces', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  async getSpace(id: string): Promise<SpaceSummary> {
    return await request<SpaceSummary>(`/spaces/${id}`);
  },
  async updateSpace(
    id: string,
    patch: Partial<{
      title: string;
      description: string | null;
      featured_images: string[];
      ue_client_download_url: string | null;
      public_type?: number;
    }>,
  ): Promise<SpaceSummary> {
    return await request<SpaceSummary>(`/spaces/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async publishSpace(id: string): Promise<void> {
    await request(`/spaces/${id}/publish`, { method: 'POST' });
  },
  async archiveSpace(id: string): Promise<void> {
    await request(`/spaces/${id}/archive`, { method: 'POST' });
  },
  async forceDisconnect(id: string): Promise<void> {
    await request(`/spaces/${id}/force-disconnect`, { method: 'POST' });
  },
  async regenerateInviteCode(
    id: string,
  ): Promise<{ invite_code: string }> {
    return await request<{ invite_code: string }>(
      `/spaces/${id}/regenerate-invite-code`,
      { method: 'POST' },
    );
  },
  async setScheduledT0(
    id: string,
    options: { armed: boolean; at: string | null },
  ): Promise<void> {
    await request(`/spaces/${id}/t0`, {
      method: 'POST',
      body: JSON.stringify(options),
    });
  },
  async listScheduledIntents(
    spaceId: string,
    status?: number,
  ): Promise<ScheduledIntent[]> {
    const qs = status !== undefined ? `?status=${status}` : '';
    return await request<ScheduledIntent[]>(
      `/spaces/${spaceId}/scheduled-intents${qs}`,
    );
  },
  async listActors(spaceId: string): Promise<ActorRow[]> {
    return await request<ActorRow[]>(`/spaces/${spaceId}/actors`);
  },
  async upsertActor(spaceId: string, actor: Partial<ActorRow>): Promise<ActorRow> {
    return await request<ActorRow>(`/spaces/${spaceId}/actors`, {
      method: 'POST',
      body: JSON.stringify(actor),
    });
  },
  async bulkUpsertActors(
    spaceId: string,
    actors: Array<Partial<ActorRow>>,
  ): Promise<ActorRow[]> {
    return await request<ActorRow[]>(`/spaces/${spaceId}/actors/bulk`, {
      method: 'POST',
      body: JSON.stringify({ actors }),
    });
  },
  async updateActor(
    actorId: string,
    patch: Partial<{
      name: string;
      class_hint: string | null;
      introduction: string | null;
      image_url: string | null;
      actor_type: number;
      tags: string[];
      status: 0 | 1;
    }>,
  ): Promise<ActorRow> {
    return await request<ActorRow>(`/actors/${actorId}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  },
  async deleteActor(actorId: string): Promise<void> {
    await request(`/actors/${actorId}`, { method: 'DELETE' });
  },
  async listActorAssignments(
    actorId: string,
  ): Promise<ActorAssignmentRow[]> {
    return await request<ActorAssignmentRow[]>(`/actors/${actorId}/assignments`);
  },
  async getSpaceStatus(spaceId: string): Promise<SpaceStatus> {
    return await request<SpaceStatus>(`/spaces/${spaceId}/status`);
  },
  async listCommandLog(
    spaceId: string,
    limit = 50,
  ): Promise<CommandLogRow[]> {
    return await request<CommandLogRow[]>(
      `/spaces/${spaceId}/command-log?limit=${limit}`,
    );
  },
  async register(
    email: string,
    password: string,
    displayName?: string,
  ): Promise<{ id: string; api_key: string; role: string }> {
    return await request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, displayName }),
    });
  },
  async login(
    email: string,
    password: string,
  ): Promise<{ id: string; displayName: string | null; role: string; api_key: string }> {
    return await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  async logout(): Promise<void> {
    await request('/auth/logout', { method: 'POST' });
  },
  async me(): Promise<{
    id: string;
    email: string;
    displayName: string | null;
    role: string;
    api_key_masked: string;
  }> {
    return await request('/auth/me');
  },
  async regenerateApiKey(): Promise<{ api_key: string }> {
    return await request('/auth/me/api-key/regenerate', {
      method: 'POST',
    });
  },
  async bootstrapEditor(
    email: string,
    password: string,
  ): Promise<{ id: string; role: string }> {
    return await request('/auth/bootstrap-editor', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  async uploadImage(
    file: File,
    purpose: 'space_image' | 'actor_image' = 'space_image',
  ): Promise<{ url: string; mime_type: string }> {
    const form = new FormData();
    form.append('file', file);
    form.append('purpose', purpose);
    const res = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      const body = await res.text();
      throw new Error(`${res.status} ${res.statusText}: ${body || '(no body)'}`);
    }
    return (await res.json()) as { url: string; mime_type: string };
  },
  async captureScreenshot(
    spaceId: string,
    resolution?: { w: number; h: number },
  ): Promise<{ url: string; width?: number; height?: number; format?: string }> {
    return await request<{ url: string; width?: number; height?: number; format?: string }>(
      `/spaces/${spaceId}/screenshot`,
      {
        method: 'POST',
        body: JSON.stringify({ resolution }),
      },
    );
  },
};
